import input from "@krystal/css/components/input.css?inline";
import { html, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@/components/alert";
import "@/components/button";
import "@/components/featured-icon";
import "@/components/icon";
import "@/components/input";
import "@/components/modal";
import "@/components/select";
import "@/components/textarea";
import "@/components/upload";
import EkoElement from "@/utilities/element";
import style from "./feedback-widget.css?inline";
import { BugFeedbackController } from "./screens/bug-feedback";
import { FeedbackTypeChoiceController } from "./screens/feedback-type-choice";
import { GeneralFeedbackController } from "./screens/general-feedback";
import { renderSuccess } from "./screens/success";
import {
  OPTIONS,
  type FeedbackWidgetCancelDetail,
  type FeedbackWidgetCloseDetail,
  type FeedbackWidgetSubmitDetail,
  type FeedbackWidgetValue,
} from "./types";

export type {
  FeedbackWidgetCancelDetail,
  FeedbackWidgetCloseDetail,
  FeedbackWidgetSubmitDetail,
  FeedbackWidgetValue,
};

@customElement("eko-feedback-widget")
class FeedbackWidget extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(input), unsafeCSS(style)];

  private _onBack = () => {
    this._errorMessage = undefined;
    this._feedbackType = undefined;
    this._step = 1;
  };

  private _onCancel = (event: Event) => {
    event.preventDefault();
    this._close();
  };

  private _bugFeedback = new BugFeedbackController(this, {
    onBack: () => this._onBack(),
    onCancel: (e: Event) => this._onCancel(e),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  @state() private _errorMessage: string | undefined = undefined;

  @state() private _feedbackType: FeedbackWidgetValue | undefined = undefined;

  @state() private _step: 1 | 2 | 3 = 1;

  private _feedbackTypeChoice = new FeedbackTypeChoiceController(this, {
    onChoose: (v) => {
      this._feedbackType = v;
      this._step = 2;
    },
  });

  private _generalFeedback = new GeneralFeedbackController(this, {
    onBack: () => this._onBack(),
    onCancel: (e: Event) => this._onCancel(e),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  @state() private _loading = false;

  private _onSuccessClose = (event: Event) => {
    event.preventDefault();
    this._close();
  };

  @property() heading = "We would love to hear your feedback!";

  @property() icon = "message-circle-01";

  @property({ reflect: true, type: Boolean }) open = false;

  @property({ attribute: "supporting-text" }) supportingText =
    "What type of feedback you have for us?";

  private _close() {
    const submitted = this._step === 3;
    const feedbackType: FeedbackWidgetCancelDetail["feedbackType"] = this._feedbackType ?? null;
    this.open = false;
    if (!submitted) {
      this.dispatchEvent(
        new CustomEvent<FeedbackWidgetCancelDetail>("cancel", {
          bubbles: true,
          composed: true,
          detail: { feedbackType },
        })
      );
    }
    this.dispatchEvent(
      new CustomEvent<FeedbackWidgetCloseDetail>("close", {
        bubbles: true,
        composed: true,
        detail: { feedbackType },
      })
    );
  }

  private _dispatchSubmit(detail: FeedbackWidgetSubmitDetail) {
    this._loading = true;
    this._errorMessage = undefined;
    this.dispatchEvent(
      new CustomEvent<FeedbackWidgetSubmitDetail>("submit", {
        bubbles: true,
        composed: true,
        detail,
      })
    );
  }

  private renderFeedbackFlow() {
    if (this._step === 1) {
      return this._feedbackTypeChoice.render({
        heading: this.heading,
        icon: this.icon,
        supportingText: this.supportingText,
      });
    }
    if (this._step === 2) {
      return this._feedbackType === "bug"
        ? this._bugFeedback.render({
            errorMessage: this._errorMessage,
            loading: this._loading,
          })
        : this._generalFeedback.render({
            errorMessage: this._errorMessage,
            icon: OPTIONS.find((o) => o.value === this._feedbackType)?.icon ?? this.icon,
            loading: this._loading,
          });
    }
    return renderSuccess(this._onSuccessClose);
  }

  render() {
    const layout = this._step === 2 ? "default" : "centered";

    return html`
      <eko-modal layout=${layout} ?open=${this.open} @cancel=${this._onCancel}>
        ${this.renderFeedbackFlow()}
      </eko-modal>
    `;
  }

  showError(message = "Something went wrong. Please try again.") {
    if (!this.open) return;

    this._loading = false;
    this._errorMessage = message;
  }

  showSuccess() {
    if (!this.open) return;

    this._loading = false;
    this._step = 3;
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has("_step") && this._step === 2) {
      (this._feedbackType === "bug" ? this._bugFeedback : this._generalFeedback).requestFocus();
    }

    if (changedProperties.has("open") && !this.open) {
      this._loading = false;
      this._errorMessage = undefined;
      this._step = 1;
      this._feedbackType = undefined;
      this._bugFeedback.reset();
      this._generalFeedback.reset();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-feedback-widget": FeedbackWidget;
  }
}

export default FeedbackWidget;
